import axios from 'axios';
import { useAuthStore } from '../store/auth';
import { useAiKeyStatus, isPersistentAiKeyError } from '../store/aiKeyStatus';

const baseURL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

// Paths where a successful response should clear any persistent AI-key error
// banner — the user just saved or removed a key, so the previous error is
// presumed resolved until the next failure proves otherwise.
const KEY_MUTATION_PATHS = ['/api/me/api-key', '/api/me/openai-key', '/api/me/ai-preferences'];

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Clear the persistent AI-key error banner when the user successfully
    // touches the key endpoints — likely the fix.
    const method = (r.config.method || '').toLowerCase();
    const path = r.config.url || '';
    if (
      (method === 'put' || method === 'delete') &&
      KEY_MUTATION_PATHS.some((p) => path.endsWith(p))
    ) {
      useAiKeyStatus.getState().clear();
    }
    return r;
  },
  (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    // Surface persistent AI-key errors into the global store so the dashboard
    // banner can render. Transient codes (rate-limited, network) are skipped
    // by isPersistentAiKeyError so they only fire toasts.
    const details = err.response?.data?.details;
    if (details?.code && isPersistentAiKeyError(details.code)) {
      useAiKeyStatus.getState().set({
        code: details.code,
        provider: details.provider,
        message: err.response?.data?.error || '',
      });
    }
    return Promise.reject(err);
  },
);
